/**
 * Pure game logic. No DOM access, no side effects — fully testable.
 *
 * The reducer takes a (state, action) and returns a new state. The view layer
 * watches state changes and renders the DOM.
 */

import type { GameState, GameStatus } from '@/types/game';
import type { Puzzle } from '@/types/puzzle';

export type GameAction =
  | { type: 'init'; puzzle: Puzzle; decodedOptions: string[]; decodedToJargon: Record<number, number> }
  | { type: 'select-jargon'; idx: number }
  | { type: 'select-decoded'; idx: number; nowMs: number }
  | { type: 'resolve-match'; jargonIdx: number; decodedIdx: number; correct: boolean; nowMs: number }
  | { type: 'tick' };

/** Initial empty state — replaced via 'init' action. */
export function emptyState(): GameState {
  return {
    puzzleId: '',
    status: 'idle',
    selectedJargon: null,
    selectedDecoded: null,
    matches: [],
    attemptsByJargon: { 0: 0, 1: 0, 2: 0, 3: 0 },
    attemptHistory: { 0: [], 1: [], 2: [], 3: [] },
    startTime: null,
    endTime: null,
    won: false,
    decodedOptions: [],
    decodedToJargon: {},
  };
}

/** Reducer. */
export function reduce(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'init': {
      return {
        ...emptyState(),
        puzzleId: action.puzzle.id,
        decodedOptions: action.decodedOptions,
        decodedToJargon: action.decodedToJargon,
      };
    }

    case 'select-jargon': {
      if (state.won) return state;
      // Don't toggle/select while a match is being resolved — prevents the
      // user's retry click from racing the in-flight resolve and deselecting.
      if (state.status === 'match-pending') return state;
      if (state.matches.some((m) => m.jargonIdx === action.idx)) return state;
      // Toggle deselect if same.
      if (state.selectedJargon === action.idx) {
        return { ...state, selectedJargon: null, status: 'idle' };
      }
      return {
        ...state,
        selectedJargon: action.idx,
        selectedDecoded: null,
        status: 'jargon-selected',
      };
    }

    case 'select-decoded': {
      if (state.won) return state;
      if (state.selectedJargon === null) return state;
      // Drop a duplicate decoded click while match-pending is still resolving.
      if (state.status === 'match-pending') return state;
      // Already matched — ignore.
      if (Object.values(state.matches).some((m) => m.decodedIdx === action.idx)) return state;
      return {
        ...state,
        selectedDecoded: action.idx,
        status: 'match-pending',
        startTime: state.startTime ?? action.nowMs,
      };
    }

    case 'resolve-match': {
      if (state.won) return state;
      const { jargonIdx, decodedIdx, correct, nowMs } = action;
      if (correct) {
        const wrongCount = state.attemptsByJargon[jargonIdx] ?? 0;
        const firstTry = wrongCount === 0;
        const newMatches = [...state.matches, { jargonIdx, decodedIdx, firstTry }];
        const newHistory = {
          ...state.attemptHistory,
          [jargonIdx]: [
            ...(state.attemptHistory[jargonIdx] ?? []),
            firstTry ? ('correct-first' as const) : ('correct-after' as const),
          ],
        };
        const won = newMatches.length === 4;
        const status: GameStatus = won ? 'won' : 'idle';
        return {
          ...state,
          matches: newMatches,
          attemptHistory: newHistory,
          selectedJargon: null,
          selectedDecoded: null,
          status,
          won,
          endTime: won ? nowMs : state.endTime,
        };
      } else {
        const wrongCount = (state.attemptsByJargon[jargonIdx] ?? 0) + 1;
        return {
          ...state,
          attemptsByJargon: { ...state.attemptsByJargon, [jargonIdx]: wrongCount },
          attemptHistory: {
            ...state.attemptHistory,
            [jargonIdx]: [...(state.attemptHistory[jargonIdx] ?? []), 'wrong'],
          },
          selectedJargon: null,
          selectedDecoded: null,
          status: 'idle',
        };
      }
    }

    case 'tick':
      return state;

    default:
      return state;
  }
}

/** Convenience: is the given decoded option correct for the given jargon? */
export function isCorrectMatch(
  state: GameState,
  jargonIdx: number,
  decodedIdx: number
): boolean {
  return state.decodedToJargon[decodedIdx] === jargonIdx;
}

/** Count of first-try matches in completed state. */
export function firstTryCount(state: GameState): number {
  return state.matches.filter((m) => m.firstTry).length;
}

/** Elapsed seconds for a completed game. */
export function elapsedSeconds(state: GameState): number {
  if (state.startTime === null || state.endTime === null) return 0;
  return Math.max(0, Math.round((state.endTime - state.startTime) / 1000));
}

/**
 * Build a stable, seeded shuffle of decoded options for a puzzle.
 *
 * Inputs:
 *   - 4 correct decodes (in jargon order)
 *   - 4 distractor decodes
 * Returns:
 *   - decodedOptions: shuffled length-8 array
 *   - decodedToJargon: optionIdx -> correct jargonIdx (or -1 for distractor)
 */
export function buildDecodedOptions(
  correctDecodes: string[],
  distractors: string[],
  seed: number
): { decodedOptions: string[]; decodedToJargon: Record<number, number> } {
  if (correctDecodes.length !== 4) throw new Error('expect 4 correct');
  if (distractors.length !== 4) throw new Error('expect 4 distractors');
  const items: Array<{ text: string; jargonIdx: number }> = [];
  correctDecodes.forEach((d, i) => items.push({ text: d, jargonIdx: i }));
  distractors.forEach((d) => items.push({ text: d, jargonIdx: -1 }));
  // Fisher-Yates with seeded RNG.
  const rng = mulberry32(seed);
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  const decodedOptions = items.map((it) => it.text);
  const decodedToJargon: Record<number, number> = {};
  items.forEach((it, idx) => {
    decodedToJargon[idx] = it.jargonIdx;
  });
  return { decodedOptions, decodedToJargon };
}

/** Tiny seeded RNG (deterministic). */
function mulberry32(a: number): () => number {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash a string to a 32-bit seed. */
export function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
